import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTimeInTimezone, formatDateInTimezone } from '../utils/dateUtils';

const HybridDraftManager = ({
  serverDrafts = [],
  localDraft = null,
  onLoadServerDraft,
  onLoadLocalDraft,
  onDeleteServerDraft,
  onClearLocal,
  onSaveToServer,
  onClose,
  currentFormData,
  loading = false
}) => {
  const { user } = useAuth();
  const [selectedTab, setSelectedTab] = useState('server'); // 'server' or 'local'
  const [searchTerm, setSearchTerm] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveNotes, setSaveNotes] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Auto-switch to local tab if there's a recovery draft and no server drafts
  useEffect(() => {
    if (localDraft && serverDrafts.length === 0) {
      setSelectedTab('local');
    }
  }, [localDraft, serverDrafts]);

  // Filter server drafts based on search
  const filteredServerDrafts = serverDrafts.filter(draft => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      (draft.customer_name && draft.customer_name.toLowerCase().includes(search)) ||
      (draft.customer_phone && draft.customer_phone.includes(search)) ||
      (draft.customer_email && draft.customer_email.toLowerCase().includes(search)) ||
      (draft.notes && draft.notes.toLowerCase().includes(search))
    );
  });

  const formatDate = (dateString) => {
    try {
      return formatDateTimeInTimezone(dateString);
    } catch {
      return 'Unknown date';
    }
  };

  const formatTimeAgo = (dateString) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return formatDateInTimezone(date);
    } catch {
      return 'Unknown';
    }
  };

  const handleSaveCurrentForm = async () => {
    if (!currentFormData) return;

    try {
      const result = await onSaveToServer(currentFormData, saveNotes);
      if (result.success) {
        setSaveNotes('');
        setShowSaveDialog(false);
      }
    } catch (error) {
      // Error handling is done in the parent component
    }
  };

  const handleDeleteDraft = async (draftId) => {
    try {
      await onDeleteServerDraft(draftId);
      setDeleteConfirm(null);
    } catch (error) {
      // Error handling is done in the parent component
    }
  };

  const hasCurrentFormContent = currentFormData && (
    currentFormData.customerName ||
    currentFormData.customerPhone ||
    currentFormData.customerEmail ||
    (currentFormData.items && currentFormData.items.length > 0)
  );

  return (
    <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Draft Manager</h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage saved order drafts and recover unsaved work
          </p>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Action Bar */}
      <div className="p-4 border-b border-gray-100 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* Tab Navigation */}
            <div className="flex bg-white rounded-lg p-1 border border-gray-200">
              <button
                onClick={() => setSelectedTab('server')}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  selectedTab === 'server'
                    ? 'bg-floral-100 text-floral-700 [html[data-scheme=dark]:not([data-appearance=fable])_&]:text-floral-900'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Saved Drafts ({serverDrafts.length})
              </button>
              <button
                onClick={() => setSelectedTab('local')}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  selectedTab === 'local'
                    ? 'bg-floral-100 text-floral-800'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Recovery {localDraft ? '(1)' : '(0)'}
              </button>
            </div>

            {/* Search for server drafts */}
            {selectedTab === 'server' && (
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search drafts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 pr-3 py-1 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-floral-500"
                />
                <svg className="w-4 h-4 text-gray-400 absolute left-2 top-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            )}
          </div>

          {/* Save Current Form Button */}
          {hasCurrentFormContent && (
            <button
              onClick={() => setShowSaveDialog(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-floral-600 [html[data-scheme=dark]:not([data-appearance=fable])_&]:bg-floral-700 text-white rounded-md hover:bg-floral-700 [html[data-scheme=dark]:not([data-appearance=fable])_&]:hover:bg-floral-800 transition-colors text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span>Save Current Form</span>
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-floral-600 mx-auto"></div>
              <p className="text-gray-500 mt-2">Loading drafts...</p>
            </div>
          </div>
        ) : selectedTab === 'server' ? (
          // Server Drafts Tab
          <div>
            {filteredServerDrafts.length === 0 ? (
              <div className="text-center py-8">
                <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {searchTerm ? 'No matching drafts' : 'No saved drafts'}
                </h3>
                <p className="text-gray-500">
                  {searchTerm
                    ? 'Try adjusting your search terms'
                    : 'Saved drafts will appear here for team access across computers'
                  }
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredServerDrafts.map((draft) => (
                  <div
                    key={draft.id}
                    className="border border-gray-200 rounded-lg p-4 hover:border-floral-300 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-2">
                          <h4 className="font-medium text-gray-900 truncate">
                            {(() => {
                              // Try database fields first
                              if (draft.customer_name) return draft.customer_name;

                              // Helper function to combine first and last name
                              const combineName = (obj) => {
                                if (!obj) return null;
                                const first = obj.firstName || '';
                                const last = obj.lastName || '';
                                const combined = `${first} ${last}`.trim();
                                // If we only have lastName, return just that (without leading space)
                                return combined || obj.lastName || obj.firstName || null;
                              };

                              // Try to extract from form_data
                              return (
                                combineName(draft.form_data?.customer) ||
                                combineName(draft.form_data?.recipient) ||
                                draft.form_data?.customerName ||
                                draft.form_data?.customer?.name ||
                                draft.form_data?.recipient?.name ||
                                'Unnamed Customer'
                              );
                            })()}
                          </h4>
                          {(draft.customer_phone || draft.form_data?.customerPhone || draft.form_data?.customer?.phone) && (
                            <span className="text-sm text-gray-500">
                              • {draft.customer_phone || draft.form_data?.customerPhone || draft.form_data?.customer?.phone}
                            </span>
                          )}
                        </div>

                        <div className="text-sm text-gray-600 space-y-1">
                          {(draft.customer_email || draft.form_data?.customerEmail || draft.form_data?.customer?.email) && (
                            <p>📧 {draft.customer_email || draft.form_data?.customerEmail || draft.form_data?.customer?.email}</p>
                          )}
                          {draft.notes && (
                            <p className="italic">"{draft.notes}"</p>
                          )}
                          <div className="flex items-center space-x-4 text-xs text-gray-500">
                            <span>Created by {draft.created_by_name || 'Unknown'}</span>
                            <span>•</span>
                            <span>{formatTimeAgo(draft.updated_at)}</span>
                            <span>•</span>
                            <span title={formatDate(draft.updated_at)}>
                              {formatDate(draft.updated_at)}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => onLoadServerDraft(draft.id)}
                          className="px-3 py-1 text-sm bg-floral-100 text-floral-700 [html[data-scheme=dark]:not([data-appearance=fable])_&]:text-floral-900 rounded-md hover:bg-floral-200 transition-colors"
                        >
                          Load
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(draft.id)}
                          className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200 [html[data-scheme=dark]:not([data-appearance=fable])_&]:bg-danger-bg [html[data-scheme=dark]:not([data-appearance=fable])_&]:text-danger-fg [html[data-scheme=dark]:not([data-appearance=fable])_&]:hover:bg-danger-bg transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          // Local Recovery Tab
          <div>
            {!localDraft ? (
              <div className="text-center py-8">
                <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No recovery data</h3>
                <p className="text-gray-500">
                  Your work is automatically saved locally for crash protection
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-floral-50 border border-floral-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <svg className="w-5 h-5 text-floral-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <h4 className="font-medium text-floral-900">Local Recovery Available</h4>
                      </div>

                      <div className="text-sm text-floral-800 space-y-1">
                        <p>Unsaved work detected from {formatTimeAgo(localDraft.timestamp)}</p>
                        {localDraft.data.customerName && (
                          <p>Customer: {localDraft.data.customerName}</p>
                        )}
                        {localDraft.data.customerPhone && (
                          <p>Phone: {localDraft.data.customerPhone}</p>
                        )}
                        <p className="text-xs">
                          Last auto-saved: {formatDate(localDraft.timestamp)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={onLoadLocalDraft}
                        className="px-4 py-2 bg-floral-700 text-white rounded-md hover:bg-floral-800 transition-colors text-sm"
                      >
                        Recover
                      </button>
                      <button
                        onClick={onClearLocal}
                        className="px-3 py-2 text-sm text-floral-700 hover:text-floral-800 [html[data-scheme=dark]:not([data-appearance=fable])_&]:text-floral-900 transition-colors"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Save Dialog Modal */}
      {showSaveDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Save Current Form as Draft</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (optional)
                </label>
                <textarea
                  value={saveNotes}
                  onChange={(e) => setSaveNotes(e.target.value)}
                  placeholder="Add notes about this draft..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-floral-500 focus:border-floral-500"
                  rows="3"
                />
              </div>

              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowSaveDialog(false);
                    setSaveNotes('');
                  }}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveCurrentForm}
                  className="px-4 py-2 bg-floral-600 [html[data-scheme=dark]:not([data-appearance=fable])_&]:bg-floral-700 text-white rounded-md hover:bg-floral-700 [html[data-scheme=dark]:not([data-appearance=fable])_&]:hover:bg-floral-800 transition-colors text-sm"
                >
                  Save Draft
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete Draft</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this draft? This action cannot be undone.
            </p>

            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteDraft(deleteConfirm)}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HybridDraftManager;